import {Choice, MultiChoice, Switch, TableRow} from 'platform/components';
import {Box, Show, Text} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {getCheckoutProfileDocumentCellKey} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {getCheckoutProfileIssueMessage} from '../../../utils/getCheckoutProfileIssueMessage';
import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';

type DocumentRow = CheckoutProfileFormValues['documents'][number];

interface Props {
  row: DocumentRow;
  paymentLabel: string;
  series: {
    label: string;
    value: string;
  }[];
  templates: {
    label: string;
    value: string;
  }[];
  onChange: (row: DocumentRow) => void;
  isLoading: boolean;
  isDisabled: boolean;
  errors?: {
    allowedSeriesIds?: {message?: string};
    defaultSeriesId?: {message?: string};
    allowedTemplateIds?: {message?: string};
    defaultTemplateId?: {message?: string};
  };
}

export function CheckoutProfileDocumentRow(props: Props) {
  const restrictSeries = props.row.restrictSeries ?? props.row.allowedSeriesIds.length > 0;
  const restrictTemplates = props.row.restrictTemplates ?? props.row.allowedTemplateIds.length > 0;
  const namePrefix = `documents.${getCheckoutProfileDocumentCellKey(props.row)}`;
  const validationMessages = [
    {column: 'paymentMethod'},
    {column: 'restrictSeries'},
    {column: 'allowedSeries', message: props.errors?.allowedSeriesIds?.message},
    {column: 'defaultSeries', message: props.errors?.defaultSeriesId?.message},
    {column: 'restrictTemplates'},
    {column: 'allowedTemplates', message: props.errors?.allowedTemplateIds?.message},
    {column: 'defaultTemplate', message: props.errors?.defaultTemplateId?.message},
    {column: 'allowOverride'},
  ];

  return (
    <>
      <TableRow>
        <Box padding={3}>
          <Text size="small">{props.paymentLabel}</Text>
        </Box>
        <Box padding={3} id={`checkout-focus-${namePrefix}.restrictSeries`} tabIndex={-1}>
          <Switch
            name={`${namePrefix}.restrictSeries`}
            aria-label={props.paymentLabel + ': ' + i18n.t('entity.checkout.labels.restrictSeries')}
            value={restrictSeries}
            isDisabled={props.isDisabled}
            onChange={(value) =>
              props.onChange({
                ...props.row,
                restrictSeries: value,
                allowedSeriesIds: value ? props.row.allowedSeriesIds : [],
                defaultSeriesId:
                  !value ||
                  (isNotNil(props.row.defaultSeriesId) &&
                    props.row.allowedSeriesIds.includes(props.row.defaultSeriesId))
                    ? props.row.defaultSeriesId
                    : undefined,
              })
            }
          />
        </Box>
        <Box padding={3}>
          <MultiChoice
            name={`${namePrefix}.allowedSeriesIds`}
            menuInPortal
            aria-label={props.paymentLabel + ': ' + i18n.t('entity.checkout.labels.allowedSeries')}
            placeholder={restrictSeries ? undefined : i18n.t('entity.checkout.labels.allSeries')}
            isRequired={restrictSeries}
            isInvalid={isNotNilOrEmpty(props.errors?.allowedSeriesIds?.message)}
            value={props.row.allowedSeriesIds}
            options={props.series}
            isLoading={props.isLoading}
            isDisabled={props.isDisabled || !restrictSeries}
            onChange={(value) => {
              const ids = value ?? [];
              props.onChange({
                ...props.row,
                allowedSeriesIds: ids,
                defaultSeriesId:
                  isNotNil(props.row.defaultSeriesId) && ids.includes(props.row.defaultSeriesId)
                    ? props.row.defaultSeriesId
                    : undefined,
              });
            }}
          />
        </Box>
        <Box padding={3}>
          <Choice
            name={`${namePrefix}.defaultSeriesId`}
            menuInPortal
            aria-label={props.paymentLabel + ': ' + i18n.t('entity.checkout.labels.defaultSeries')}
            value={props.row.defaultSeriesId ?? null}
            options={
              restrictSeries
                ? props.series.filter((item) => props.row.allowedSeriesIds.includes(item.value))
                : props.series
            }
            isDisabled={props.isDisabled}
            isInvalid={isNotNilOrEmpty(props.errors?.defaultSeriesId?.message)}
            onChange={(value) =>
              props.onChange({...props.row, defaultSeriesId: value ?? undefined})
            }
          />
        </Box>
        <Box padding={3} id={`checkout-focus-${namePrefix}.restrictTemplates`} tabIndex={-1}>
          <Switch
            name={`${namePrefix}.restrictTemplates`}
            aria-label={
              props.paymentLabel + ': ' + i18n.t('entity.checkout.labels.restrictTemplates')
            }
            value={restrictTemplates}
            isDisabled={props.isDisabled}
            onChange={(value) =>
              props.onChange({
                ...props.row,
                restrictTemplates: value,
                allowedTemplateIds: value ? props.row.allowedTemplateIds : [],
                defaultTemplateId:
                  !value ||
                  (isNotNil(props.row.defaultTemplateId) &&
                    props.row.allowedTemplateIds.includes(props.row.defaultTemplateId))
                    ? props.row.defaultTemplateId
                    : undefined,
              })
            }
          />
        </Box>
        <Box padding={3}>
          <MultiChoice
            name={`${namePrefix}.allowedTemplateIds`}
            menuInPortal
            aria-label={
              props.paymentLabel + ': ' + i18n.t('entity.checkout.labels.allowedTemplates')
            }
            placeholder={
              restrictTemplates ? undefined : i18n.t('entity.checkout.labels.allTemplates')
            }
            isRequired={restrictTemplates}
            isInvalid={isNotNilOrEmpty(props.errors?.allowedTemplateIds?.message)}
            value={props.row.allowedTemplateIds}
            options={props.templates}
            isLoading={props.isLoading}
            isDisabled={props.isDisabled || !restrictTemplates}
            onChange={(value) => {
              const ids = value ?? [];
              props.onChange({
                ...props.row,
                allowedTemplateIds: ids,
                defaultTemplateId:
                  isNotNil(props.row.defaultTemplateId) && ids.includes(props.row.defaultTemplateId)
                    ? props.row.defaultTemplateId
                    : undefined,
              });
            }}
          />
        </Box>
        <Box padding={3}>
          <Choice
            name={`${namePrefix}.defaultTemplateId`}
            menuInPortal
            aria-label={props.paymentLabel + ': ' + i18n.t('entity.gdpr.labels.defaultTemplate')}
            isInvalid={isNotNilOrEmpty(props.errors?.defaultTemplateId?.message)}
            value={props.row.defaultTemplateId ?? null}
            options={
              restrictTemplates
                ? props.templates.filter((item) =>
                    props.row.allowedTemplateIds.includes(item.value)
                  )
                : props.templates
            }
            isDisabled={props.isDisabled}
            onChange={(value) =>
              props.onChange({...props.row, defaultTemplateId: value ?? undefined})
            }
          />
        </Box>
        <Box padding={3}>
          <Switch
            name={`${namePrefix}.isOverrideAllowed`}
            aria-label={props.paymentLabel + ': ' + i18n.t('entity.checkout.labels.allowOverride')}
            value={props.row.isOverrideAllowed}
            isDisabled={props.isDisabled}
            onChange={(value) => props.onChange({...props.row, isOverrideAllowed: value})}
          />
        </Box>
      </TableRow>
      <Show when={validationMessages.some((item) => isNotNilOrEmpty(item.message))}>
        <tr>
          {validationMessages.map((item) => (
            <td key={item.column}>
              <Show when={isNotNilOrEmpty(item.message) ? item.message : null}>
                {(message) => (
                  <Box paddingHorizontal={3} paddingBottom={3}>
                    <Text role="alert" size="xSmall" color="danger">
                      {getCheckoutProfileIssueMessage(message, message)}
                    </Text>
                  </Box>
                )}
              </Show>
            </td>
          ))}
        </tr>
      </Show>
    </>
  );
}
